import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUserContext } from "../context/UserContext";
import { deleteAccount } from "../services/UserService";
import { logOut } from "../services/AuthService";
import { toast } from "react-toastify";
import { AlertTriangle, X, Loader2, CreditCard } from "lucide-react";
import { resolveDeleteAccountSubscriptionWarning } from "../utils/deleteAccount.logic";

export default function DeleteAccountPage() {
  const { user } = useUserContext();
  const navigate = useNavigate();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      await deleteAccount();
      toast.success("Your account has been deleted successfully");
      // Log out the user
      await logOut();
      // Redirect to landing page
      navigate("/");
    } catch (error: any) {
      console.error("Failed to delete account:", error);
      toast.error(
        error?.message || "Failed to delete account. Please try again."
      );
      setIsDeleting(false);
    }
  };

  if (!user) {
    return null;
  }

  const subscriptionWarning = resolveDeleteAccountSubscriptionWarning({
    retailers: user.retailers,
    subscriptionSrc: user.subscriptionSrc,
    customerId: user.customerId,
  });

  const subscriptionWarningBox =
    subscriptionWarning.kind === "none" ? null : (
      <div
        data-testid="delete-account-subscription-warning"
        className="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg p-4"
      >
        <h3 className="font-semibold text-amber-900 dark:text-amber-300 mb-1 flex items-center gap-2">
          <CreditCard className="h-4 w-4" />
          {subscriptionWarning.title}
        </h3>
        <p className="text-sm text-amber-900/90 dark:text-amber-200/90 mb-3">{subscriptionWarning.body}</p>
        <button
          type="button"
          onClick={() => navigate("/settings?tab=billing")}
          disabled={isDeleting}
          className="w-full bg-primary-600 hover:bg-primary-700 text-white font-semibold py-2.5 px-4 rounded-lg transition-colors disabled:opacity-50"
        >
          {subscriptionWarning.actionLabel}
        </button>
      </div>
    );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-950 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white dark:bg-dark-800 rounded-xl shadow-sm p-6">
        <div className="text-center mb-6">
          <div className="mx-auto w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-4">
            <AlertTriangle className="h-8 w-8 text-red-600 dark:text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Delete Account
          </h1>
          <p className="text-sm text-gray-600 dark:text-dark-300">
            This action cannot be undone. All your data will be permanently
            deleted.
          </p>
        </div>

        <div className="space-y-4 mb-6">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <h3 className="font-semibold text-red-900 dark:text-red-300 mb-2">
              What will be deleted:
            </h3>
            <ul className="text-sm text-red-800 dark:text-red-300/80 space-y-1 list-disc list-inside">
              <li>Your account and profile information</li>
              <li>All your favorites</li>
              <li>All your price alerts</li>
              <li>All your saved data</li>
              <li>Your access to your paid plan</li>
            </ul>
          </div>
          {subscriptionWarningBox}
        </div>

        <button
          onClick={() => setShowConfirmModal(true)}
          disabled={isDeleting}
          className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Delete My Account
        </button>

        <button
          onClick={() => navigate("/settings")}
          className="w-full mt-3 bg-gray-200 dark:bg-dark-700 hover:bg-gray-300 dark:hover:bg-dark-600 text-gray-800 dark:text-dark-200 font-semibold py-3 px-4 rounded-lg transition-colors"
        >
          Cancel
        </button>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[10000]">
          <div className="bg-white dark:bg-dark-800 rounded-2xl max-w-sm w-full p-6 shadow-2xl">
            {/* Close Button */}
            <button
              type="button"
              onClick={() => setShowConfirmModal(false)}
              disabled={isDeleting}
              className="absolute top-4 right-4 text-gray-400 dark:text-dark-400 hover:text-gray-600 dark:hover:text-dark-200 transition-colors p-1 z-10 disabled:opacity-50"
            >
              <X size={20} />
            </button>

            {/* Icon and Title */}
            <div className="text-center mb-6">
              <div className="mx-auto w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-3">
                <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                Are you sure?
              </h2>
              <p className="text-sm text-gray-600 dark:text-dark-300">
                This will permanently delete your account and all your data.
                This action cannot be undone.
              </p>
            </div>

            {subscriptionWarning.kind !== "none" && (
              <p className="text-sm font-medium text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 mb-4">
                {subscriptionWarning.kind === "store"
                  ? "Reminder: this will not cancel your App Store / Google Play subscription."
                  : "Reminder: you have an active subscription. Deleting removes your access immediately \u2014 cancel from Billing instead if you just want to stop paying."}
              </p>
            )}

            {/* Actions */}
            <div className="space-y-3">
              <button
                onClick={handleDeleteAccount}
                disabled={isDeleting}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isDeleting && <Loader2 className="w-4 h-4 animate-spin" />}
                {isDeleting ? "Deleting..." : "Yes, Delete My Account"}
              </button>
              <button
                onClick={() => setShowConfirmModal(false)}
                disabled={isDeleting}
                className="w-full bg-gray-200 dark:bg-dark-700 hover:bg-gray-300 dark:hover:bg-dark-600 text-gray-800 dark:text-dark-200 font-semibold py-3 px-4 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

